import { company } from '@awpmarine/core/company';
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@awpmarine/ui/components/ui/item';
import { createFileRoute } from '@tanstack/react-router';
import { CheckIcon, MailIcon, PhoneIcon } from 'lucide-react';

import { Eyebrow } from '@/components/marketing';
import { pageHead } from '@/lib/head';
import { ogImage } from '@/lib/open-graph';
import { m } from '@/paraglide/messages';
import type { MessageFunction } from '@/paraglide/runtime';

import { ContactPageForm } from './-form';

export const metadata = () => ({ title: m.contact_title(), description: m.contact_description() });

export const Route = createFileRoute('/contact/')({
  head: () => pageHead({ ...metadata(), canonical: '/contact', image: ogImage('/contact') }),
  component: ContactPage,
});

// Message functions, read when the page renders so the text follows its locale.
const tips = [m.contact_tip_vessel, m.contact_tip_port, m.contact_tip_service, m.contact_tip_deadline];

/** The phone labels in @awpmarine/core/company, which the emails and PDFs share in English. */
const phoneLabels: Record<string, MessageFunction> = {
  Office: m.contact_phone_office,
  Mobile: m.contact_phone_mobile,
};

function ContactPage() {
  const details = [
    ...company.phones.map((phone) => ({
      icon: PhoneIcon,
      label: phoneLabels[phone.label]?.() ?? phone.label,
      value: phone.display,
      href: `tel:${phone.tel}`,
    })),
    {
      icon: MailIcon,
      label: m.contact_email(),
      value: company.emails.default,
      href: `mailto:${company.emails.default}`,
    },
  ];

  return (
    <section className='bg-mist'>
      <div className='mx-auto flex w-full max-w-310 flex-wrap items-start gap-12 px-5 pt-14 pb-16 sm:px-8 sm:pt-18 sm:pb-24'>
        <div className='min-w-0 flex-[1_1_380px]'>
          <Eyebrow>{m.contact_eyebrow()}</Eyebrow>
          <h1 className='mb-5 text-[clamp(38px,4.4vw,56px)] leading-[1.08] font-bold tracking-[-0.02em]'>
            {m.contact_heading()}
          </h1>
          <p className='text-body mb-10 max-w-115 text-[19px] leading-[1.65]'>{m.contact_lead()}</p>
          <div className='mb-10 flex flex-col gap-5.5'>
            {details.map((detail) => (
              <Item
                key={detail.href}
                render={<a href={detail.href} />}
                className='items-start gap-4 border-0 p-0 text-base [a]:hover:bg-transparent'
              >
                <ItemMedia className='size-11 translate-y-0 rounded-[14px] bg-white text-brand group-has-data-[slot=item-description]/item:translate-y-0'>
                  <detail.icon className='size-5' />
                </ItemMedia>
                <ItemContent className='gap-0'>
                  <ItemDescription className='text-sm'>{detail.label}</ItemDescription>
                  <ItemTitle className='text-[17px] leading-normal font-semibold'>{detail.value}</ItemTitle>
                </ItemContent>
              </Item>
            ))}
          </div>
          <div className='border-t border-[#dfe5ed] pt-7'>
            <h2 className='mb-3.5 font-bold'>{m.contact_tips_title()}</h2>
            <ul className='text-body flex flex-col gap-2.5'>
              {tips.map((tip) => (
                <li key={tip()} className='flex items-start gap-2.5'>
                  <CheckIcon className='mt-0.5 size-4.5 shrink-0 text-brand' />
                  {tip()}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className='min-w-0 flex-[1.2_1_520px]'>
          <ContactPageForm />
        </div>
      </div>
    </section>
  );
}
